import { useState } from 'react';
import { gql, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import {
    Button,
    Checkbox,
    Form,
    FormField,
    FormInput,
    Message,
    Radio,
} from 'semantic-ui-react';

import { formatDateForBirth } from '../../../dates';
import { PATHS } from '../../../paths';
import { GENDER_OPTIONS, isMinor, missingForRegistration } from '../../../utils/registration';
import { CREATE_PERSON_WITH_REGISTRATION } from '../../../mutations/PERSON_REGISTRATION';
import { usePersonDocumentUpload, type PendingDocument } from '../../../hooks/usePersonDocumentUpload';

import { CommunityPicker, resolveCommunities, useCommunityData } from './CommunityPicker';
import { EmergencyContactSection, EMPTY_EMERGENCY_CONTACT, type EmergencyContactState } from './EmergencyContactSection';
import { HealthSection } from './HealthSection';

import { ConsentSection, EMPTY_CONSENT, type ConsentState } from './ConsentSection';
import { EMPTY_GUARDIAN, GuardianSection, type GuardianState } from './GuardianSection';
import { SupportingDocuments } from './SupportingDocuments';

const GET_ORG_SCRIPT_LABEL = gql`
query OrganisationScriptLabel {
  me {
    organisation {
      scriptLabel
    }
  }
}`;

const GET_UPLOAD_URLS = gql`
query PersonUploadUrls($personId: Int!) {
  user(personId: $personId) {
    id
    uploadURL
    uploadURLSmall
  }
}`;

/**
 * The registration form.
 *
 * Fields are in a **logical** order rather than the paper form's — identity, then the
 * communities someone belongs to, then what they consented to, then (for a child) who consented
 * on their behalf, then attachments. The brief explicitly released the "same order as the form"
 * constraint, and this grouping is what makes the greyed guardian section legible: it sits
 * immediately after the consents it substitutes for.
 *
 * The classic form is untouched and still reachable from the toggle above. Both call the same
 * `adminCreateUser` mutation; this one simply fills in arguments the other leaves null.
 */

const NewPersonCreator = () => {
    const navigate = useNavigate();

    // --- identity ---
    const [name, setName] = useState('');
    const [preferredName, setPreferredName] = useState('');
    const [scriptName, setScriptName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [dob, setDob] = useState<Date | null>(null);
    const [graphqlDob, setGraphqlDob] = useState('');
    const [gender, setGender] = useState('');
    const [photoFile, setPhotoFile] = useState<File | null>(null);

    /**
     * Whether picture permission has been answered in its own right.
     *
     * Lives here rather than in `ConsentSection` so it survives a re-render of that component,
     * and is deliberately not sent to the server: it describes how this form was filled in,
     * not a fact about the person.
     */
    const [picturePermissionTouched, setPicturePermissionTouched] = useState(false);

    /** Answers the phone question for someone who has none, so it does not block them. */
    const [noPhone, setNoPhone] = useState(false);

    // --- communities & identifier ---
    // One list, in pick order. The identifier is *derived* from it — never typed or picked — so
    // there is no first box for a programme to land in. See CommunityPicker.
    const [communityIds, setCommunityIds] = useState<number[]>([]);
    const [homeLocationId, setHomeLocationId] = useState<number | null>(null);
    const communityData = useCommunityData();
    const { coding, identifier } = resolveCommunities(communityData, communityIds, homeLocationId);

    // --- emergency contact & health ---
    const [emergency, setEmergency] = useState<EmergencyContactState>(EMPTY_EMERGENCY_CONTACT);
    const [healthInformation, setHealthInformation] = useState('');

    // --- consent & guardian ---
    const [consent, setConsent] = useState<ConsentState>(EMPTY_CONSENT);
    const [guardian, setGuardian] = useState<GuardianState>(EMPTY_GUARDIAN);

    // --- documents ---
    const [documents, setDocuments] = useState<PendingDocument[]>([]);

    const [showErrors, setShowErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState('');

    const [createPersonMutation, { loading }] = useMutation(CREATE_PERSON_WITH_REGISTRATION);
    const [fetchUploadUrls] = useLazyQuery(GET_UPLOAD_URLS, { fetchPolicy: 'network-only' });
    const uploadDocuments = usePersonDocumentUpload();

    const { data: orgData } = useQuery(GET_ORG_SCRIPT_LABEL);
    const scriptLabel = orgData?.me?.organisation?.scriptLabel || 'Bengali';

    // What this form requires — in the form only. The server stays as permissive as it was,
    // because the classic form shares `adminCreateUser` and must keep saving unchanged. See
    // `missingForRegistration` for what is and is not on the list, and why.
    const personIsMinor = isMinor(dob);
    const missing = missingForRegistration({
        name,
        dob: graphqlDob,
        coding: coding.status,
        phone,
        noPhone,
        emergencyContactName: emergency.emergencyContactName,
        emergencyContactPhone: emergency.emergencyContactPhone,
        consentDataCollection: consent.consentDataCollection,
        healthSafetyBriefed: consent.healthSafetyBriefed,
        consentHealthSafety: consent.consentHealthSafety,
        isMinor: personIsMinor,
        guardianConsentsToChildData: guardian.guardianConsentsToChildData,
    });
    const isComplete = missing.length === 0;

    const uploadPhoto = async (personId: number) => {
        if (!photoFile) return;
        try {
            const { data } = await fetchUploadUrls({ variables: { personId } });
            const urls = [data?.user?.uploadURL, data?.user?.uploadURLSmall].filter(Boolean) as string[];
            await Promise.all(urls.map((url) => fetch(url, {
                method: 'PUT',
                body: photoFile,
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Length': photoFile.size.toString(),
                },
                referrer: 'no-referrer',
            })));
        } catch (photoErr) {
            // The person was created successfully; only the photo failed. Don't block navigation.
            console.error('Photo upload failed', photoErr);
        }
    };

    const handleSave = async () => {
        if (!isComplete) {
            setShowErrors(true);
            return;
        }

        setErrorMessage('');
        setWarningMessage('');

        try {
            const result = await createPersonMutation({
                variables: {
                    name,
                    preferredName,
                    address,
                    scriptName,
                    identifier,
                    phone,
                    dob: graphqlDob,
                    gender: gender || null,
                    // The community that codes the identifier first, then the rest. The server
                    // checks the identifier against the whole set, not the order.
                    tagIds: coding.status === 'ok'
                        ? [coding.community.id, ...communityIds.filter((id) => id !== coding.community.id)]
                        : communityIds,
                    picturePermission: consent.picturePermission,
                    // Only this form sends it, and it is what stamps `completedAt` — people
                    // added on the classic form deliberately get no registration row at all.
                    registrationCompleted: true,
                    // The three retired data-use columns are deliberately not sent: the server
                    // derives them from `consentDataCollection`, so sending them would be a
                    // second source for one answer.
                    consentDataCollection: consent.consentDataCollection,
                    healthSafetyBriefed: consent.healthSafetyBriefed,
                    consentHealthSafety: consent.consentHealthSafety,
                    idSightedByCM: consent.idSightedByCM,
                    ...guardian,
                    ...emergency,
                    healthInformation: healthInformation.trim() || null,
                },
            });

            const newId = result.data.adminCreateUser.id;

            await uploadPhoto(newId);

            const failed = await uploadDocuments(newId, documents);

            if (failed.length > 0) {
                // The person is saved. Say which attachments did not make it **and why**, and
                // still go to the profile — they can be re-added there.
                setWarningMessage(
                    `Saved, but these documents did not upload — ${
                        failed.map((f) => `${f.fileName}: ${f.reason}`).join(' · ')
                    }`
                );
            }

            navigate(`/people/${newId}`);
        } catch (e: any) {
            setErrorMessage(e?.message || 'Something went wrong creating this person.');
        }
    };

    return (
        <Form error={!!errorMessage} size="small">
            {/* ------------------------------------------------------------- identity */}
            <div className="creator-section">
                <div className="creator-section-title">Identity</div>

                <CommunityPicker
                    data={communityData}
                    selectedIds={communityIds}
                    onSelectedIdsChange={setCommunityIds}
                    homeLocationId={homeLocationId}
                    onHomeLocationIdChange={setHomeLocationId}
                    showErrors={showErrors}
                />

                <Form.Group widths="equal">
                    <FormInput
                        error={showErrors && name.length < 1 && 'Please enter a name'}
                        fluid
                        label="Name"
                        placeholder="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <FormInput
                        fluid
                        label="Preferred name"
                        placeholder="Preferred name"
                        value={preferredName}
                        onChange={(e) => setPreferredName(e.target.value)}
                    />
                </Form.Group>

                <Form.Group widths="equal">
                    <FormInput
                        fluid
                        label={`Name in ${scriptLabel}`}
                        placeholder=""
                        value={scriptName}
                        onChange={(e) => setScriptName(e.target.value)}
                    />
                    <FormField error={showErrors && phone.trim() === '' && !noPhone}>
                        <label htmlFor="registration-phone">Phone</label>
                        <input
                            id="registration-phone"
                            type="text"
                            placeholder="Phone number"
                            disabled={noPhone}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                        {/* An answer, not a skip: "has no phone" is a fact about them, and without
                            it the field would block registering someone who genuinely has none. */}
                        <Checkbox
                            id="registration-no-phone"
                            label="No phone"
                            checked={noPhone}
                            onChange={(_, d) => {
                                setNoPhone(!!d.checked);
                                if (d.checked) setPhone('');
                            }}
                            style={{ marginTop: '0.5em' }}
                        />
                    </FormField>
                </Form.Group>

                <Form.Group widths="equal">
                    <FormField error={showErrors && graphqlDob.length < 1}>
                        <label>Date of birth</label>
                        <DatePicker
                            selected={dob}
                            dateFormat="dd/MM/yyyy"
                            onChange={(date) => {
                                setGraphqlDob(date != null ? formatDateForBirth(date?.getTime()) : '');
                                setDob(date);
                            }}
                            showYearDropdown
                            maxDate={new Date()}
                        />
                        {showErrors && graphqlDob.length < 1 && (
                            <div className="ui pointing above prompt label">Please enter a date of birth</div>
                        )}
                    </FormField>
                    <FormField>
                        <label>Gender</label>
                        <div className="sex-options">
                            {GENDER_OPTIONS.map((option) => (
                                <Radio
                                    key={option.value}
                                    label={option.label}
                                    name="genderRadioGroup"
                                    value={option.value}
                                    checked={gender === option.value}
                                    onChange={() => setGender(option.value)}
                                />
                            ))}
                        </div>
                    </FormField>
                </Form.Group>

                {/* Optional since 2026-11-03 — it was required while phone was not, which is the
                    wrong way round for reaching someone. */}
                <FormInput
                    fluid
                    label="Address"
                    placeholder="Address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                />

                <FormField>
                    <label>Photo</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                    />
                </FormField>
            </div>

            {/* -------------------------------------------------------------- consent */}
            <ConsentSection
                value={consent}
                onChange={setConsent}
                picturePermissionTouched={picturePermissionTouched}
                onPicturePermissionTouched={() => setPicturePermissionTouched(true)}
            />

            {/* ------------------------------------------------------------- guardian */}
            <GuardianSection value={guardian} onChange={setGuardian} personDob={dob} />

            {/* ---------------------------------------------------- emergency contact */}
            {/* After the guardian, so "Same as parent/guardian" has something above it to copy. */}
            <EmergencyContactSection
                value={emergency}
                onChange={setEmergency}
                guardian={guardian}
                personIsMinor={personIsMinor}
                showErrors={showErrors}
            />

            {/* --------------------------------------------------------------- health */}
            <HealthSection value={healthInformation} onChange={setHealthInformation} />

            {/* ------------------------------------------------------------ documents */}
            <SupportingDocuments documents={documents} onChange={setDocuments} disabled={loading} />

            {/* Why it will not save, in the form's own order. Shown once they have tried, so the
                list answers "why didn't that work" rather than being a wall before they start. */}
            {showErrors && !isComplete && (
                <Message warning visible header="Still needed before saving" list={missing} />
            )}

            <Message error header="Could not save" content={errorMessage} />
            {warningMessage && <Message warning visible content={warningMessage} />}

            {/* Enabled while incomplete on purpose: clicking is what shows the list above and the
                field highlights. A disabled button with no reason is the thing this replaces. */}
            <Button
                primary
                type="button"
                loading={loading}
                disabled={loading}
                onClick={() => handleSave()}
            >
                Save
            </Button>
            <Button
                type="button"
                basic
                disabled={loading}
                onClick={() => navigate(PATHS.PEOPLE)}
            >
                Cancel
            </Button>
        </Form>
    );
};

export { NewPersonCreator };
